import type { ParcellingMaturationCycle } from '@practium/features/parcelling';
import { MATURATION_CYCLE_OPTIONS } from '../../parcelling/constants/maturationCycles.constants';
import type {
	FertilizationPlanAdvice,
	MaintenanceFormData,
	MaintenanceParcellingType,
} from '../types';

const NONE_VALUE = 'none';

export const getMaintenanceReferenceId = (value: unknown) => {
	if (typeof value === 'string') return value;
	if (!value || typeof value !== 'object') return '';
	const reference = value as { _id?: unknown; id?: unknown };
	const id = reference._id ?? reference.id;
	if (typeof id === 'string') return id;
	if (id == null) return '';
	const asString = String(id);
	return asString && asString !== '[object Object]' ? asString : '';
};

export const getMaintenanceReferenceName = (
	value: unknown,
	fallback = ''
) => {
	if (value && typeof value === 'object') {
		const reference = value as { name?: unknown };
		if (typeof reference.name === 'string') return reference.name;
	}
	return fallback || (typeof value === 'string' ? value : '');
};

export const getMaintenanceSectorNames = (sectors: FertilizationPlanAdvice['sectors'] = []) =>
	sectors.map((sector) => getMaintenanceReferenceName(sector)).filter(Boolean);

export const normalizeMaintenanceMonth = (value?: string) => {
	if (!value) return '';
	const normalized = value.replace(/\//g, '-');
	return /^\d{4}-\d{2}/.test(normalized) ? normalized.slice(0, 7) : '';
};

export const getMaintenanceMonths = (periods: string[] = []) =>
	[...new Set(periods.map((period) => period.replace(/-/g, '/').slice(0, 7)))].sort();

export const getMaintenanceParcellingType = (
	advice: Pick<FertilizationPlanAdvice, 'parcellingType' | 'parcelling'>
): MaintenanceParcellingType | '' => {
	if (advice.parcellingType === 'fixed' || advice.parcellingType === 'dynamic') {
		return advice.parcellingType;
	}
	if (advice.parcelling && typeof advice.parcelling === 'object') {
		const type = (advice.parcelling as { typeParcelling?: unknown }).typeParcelling;
		if (type === 'fixed' || type === 'dynamic') return type;
	}
	return '';
};

export const getMaintenanceMaturationCycle = (value: unknown): ParcellingMaturationCycle | '' => {
	const cycle =
		value && typeof value === 'object'
			? (value as { maturationCycle?: unknown }).maturationCycle
			: value;
	return MATURATION_CYCLE_OPTIONS.find((option) => option.value === cycle)?.value ?? '';
};

export const normalizeMaintenanceDate = (value?: string) =>
	/^\d{4}-\d{2}-\d{2}/.test(value ?? '') ? (value ?? '').slice(0, 10) : '';

export const mapMaintenanceAdviceToForm = (
	advice: FertilizationPlanAdvice
): MaintenanceFormData => ({
	name: advice.name ?? '',
	prop: getMaintenanceReferenceId(advice.prop),
	area: getMaintenanceReferenceId(advice.area),
	sectors: advice.sectors.map(getMaintenanceReferenceId).filter(Boolean),
	soilAnalysis: getMaintenanceReferenceId(advice.soilAnalysis),
	plantingAdvice: getMaintenanceReferenceId(advice.plantingAdvice) || NONE_VALUE,
	cropStage: advice.cropStage ?? '',
	productivity: advice.productivity == null ? '' : String(advice.productivity),
	parcellingType: getMaintenanceParcellingType(advice),
	parcelling: getMaintenanceReferenceId(advice.parcelling),
	floweringMonth:
		normalizeMaintenanceMonth(advice.floweringMonth) ||
		normalizeMaintenanceMonth(advice.floweringDate) ||
		'',
	floweringDate: normalizeMaintenanceDate(advice.floweringDate),
	maturationCycle: getMaintenanceMaturationCycle(advice.parcelling),
});
